import React from 'react';
import {path} from './path';

// All pages that rely on 3rd party components (other than Material-UI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Guards
import AuthRbacGuard from '../components/Guards/AuthRbacGuard';

// Layouts
import AuthLayout from '../layouts/Auth';
import DashboardLayout from '../layouts/Dashboard';
import IslandPartner from '../pages/island-partner/IslandPartner';
import SDCAdministrators from '../pages/sdc-administrators/SDCAdministrators';
import IslandMembers from '../pages/islander-members/IslanderMembers';
import AllTransaction from '../pages/all-transaction/AllTransaction';
import VoucherTransactionHistory from '../pages/voucher-transaction-history/VoucherTransactionHistory';
import Dashboard from '../pages/dashboard/Dashboard';
import Voucher from '../pages/voucher/Voucher';
import Event from '../pages/event/Event';
import Article from '../pages/article/Article';
import FeaturedVoucher from '../pages/featured-voucher/FeaturedVoucher';
import AuditLogs from '../pages/audit-logs/AuditLogs';
import AuditLogsMembers from '../pages/audit-logs-members/AuditLogsMembers';
import WatchlistTags from '../pages/watchlist-tags/WatchlistTags';
import Partnership from '../pages/referral-programme/partnership/Partnership';
import ReferralTransactions from '../pages/referral-programme/referral-transactions/ReferralTransactions';
import ReferralIslanderMember from '../pages/referral-programme/islander-member/IslanderMember';
import TargetedDistributions from '../pages/targeted-distributions/TargetedDistributions';

// detail
import SDCAdministratorsDetail from '../pages/sdc-administrators/SDCAdministratorsDetail/SDCAdministratorsDetail';
import IslanderMembersDetail from '../pages/islander-members/IslanderMemberDetail/IslanderMemberDetail';
import IslandPartnerDetail from '../pages/island-partner/IslandPartnerDetail/IslandPartnerDetail';
import AllTransactionDetail from '../pages/all-transaction/AllTransactionDetail/AllTransactionDetail';
import VoucherTransactionHistoryDetail from '../pages/voucher-transaction-history/VoucherTransactionHistoryDetail/VoucherTransactionHistoryDetail';
import VoucherDetail from '../pages/voucher/VoucherDetail/VoucherDetail';
import PartnershipDetail from '../pages/referral-programme/partnership/PartnershipDetail/PartnershipDetail';
import ReferralTransactionDetail from '../pages/referral-programme/referral-transactions/ReferralTransactionDetail/ReferralTransactionDetail';

// Auth components
import SignIn from '../pages/auth/SignIn/SignIn';
import SignUp from '../pages/auth/SignUp/SignUp';
import ResetPassword from '../pages/auth/ResetPassword/ResetPassword';
import ResetPasswordSuccess from '../pages/auth/ResetPasswordSuccess/ResetPasswordSuccess';
import ChangePasswordSuccess from '../pages/auth/ChangePasswordSuccess/ChangePasswordSuccess';

// User
import UserResetPassword from '../pages/user/ResetPassword/ResetPassword';

import Page404 from '../pages/auth/Page404/Page404';
import Page500 from '../pages/auth/Page500/Page500';
import ResetPasswordExpired from '../pages/auth/ResetPasswordExpired/ResetPasswordExpired';
import FAVerification from '../pages/auth/2FAVerification/2FAVerification';
import ForgotPassword from '../pages/auth/ForgotPassword/ForgotPassword';
import ChangeLog from '../pages/change-log/ChangeLog';
import LimitedEvent from '../pages/limited-events/LimitedEvent';
import ManageAssets from '../pages/manage-assets/ManageAssets';
import UploadNew from '../pages/upload-new/UploadNew';
import ReceiptValidationLogs from '../pages/receipt-validation-logs/ReceiptValidationLogs';
import MemberDashboard from '../pages/member-dashboard/MemberDashboard';
import MemberDashboardCreateNew from '../pages/member-dashboard/CreateNew/MemberDashboardCreateNew';
import VoucherCategories from '../pages/voucher-categories/VoucherCategories';
import AddNew from '../pages/voucher-categories/AddNew/AddNew';
import TerminatedMembers from '../pages/terminated-members/TerminatedMembers';
import DistributionLogs from '../pages/distribution-logs/DistributionLogs';
import NoAccess from '../pages/no-access/NoAccess';
import {rbacPermissions} from '../constants/roleBasedAccessControlConstants';
import {ExtendedRouteObject} from '../types/typeRoutes';

const withAuth = props => <AuthRbacGuard>{props}</AuthRbacGuard>;

const routes: ExtendedRouteObject[] = [
  {
    path: path.USER,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [],
    }),
    children: [
      {
        path: path.RESET_PASSWORD,
        element: <UserResetPassword />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [],
    }),
    children: [
      {
        path: path.DASHBOARD,
        element: <Dashboard />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.ADMINISTRATORS__LISTING__VIEW],
    }),
    children: [
      {
        path: path.PORTAL_USERS,
        element: <SDCAdministrators />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.ISLANDER_MEMBERS__LISTING__VIEW,
        rbacPermissions.ISLANDER_MEMBERS__TERMINATED_MEMBERS__VIEW,
        rbacPermissions.ISLANDER_MEMBERS__WATCHLIST_TAGS__VIEW,
      ],
    }),
    children: [
      {
        path: path.ISLAND_MEMBERS,
        requiredPermissions: [rbacPermissions.ISLANDER_MEMBERS__LISTING__VIEW],
        element: <IslandMembers />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.ISLANDER_MEMBERS__TERMINATED_MEMBERS__VIEW,
      ],
    }),
    children: [
      {
        path: `${path.ISLAND_MEMBERS}/${path.TERMINATED_MEMBERS}`,
        element: <TerminatedMembers />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.ISLANDER_MEMBERS__WATCHLIST_TAGS__VIEW,
      ],
    }),
    children: [
      {
        path: `${path.ISLAND_MEMBERS}/${path.WATCHLIST_TAG}`,
        element: <WatchlistTags />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.ISLAND_PARTNERS__LISTING__VIEW],
    }),
    children: [
      {
        path: path.ISLAND_PARTNERS,
        element: <IslandPartner />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.ISLAND_PARTNERS__LISTING__VIEW],
    }),
    children: [
      {
        path: `${path.ISLAND_PARTNERS}?merchantId=:id`,
        element: <AllTransaction />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.POINTS_TRANSACTIONS__LISTING__VIEW,
        rbacPermissions.POINTS_TRANSACTIONS__RECEIPT_VALIDATION__VIEW,
      ],
    }),
    children: [
      {
        path: path.ALL_TRANSACTION,
        element: <AllTransaction />,
        requiredPermissions: [
          rbacPermissions.POINTS_TRANSACTIONS__LISTING__VIEW,
        ],
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.POINTS_TRANSACTIONS__RECEIPT_VALIDATION__VIEW,
      ],
    }),
    children: [
      {
        path: `${path.ALL_TRANSACTION}/${path.RECEIPT_VALIDATION_LOGS}`,
        element: <ReceiptValidationLogs />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.POINTS_MULTIPLIER_EVENT__LISTING__VIEW,
      ],
    }),
    children: [
      {
        path: path.LIMITED_EVENT,
        element: <LimitedEvent />,
      },
    ],
  },
  {
    path: `${path.MOBILE_APP_CMS}/${path.MEMBER_DASHBOARD}`,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.MOBILE_APP_CMS__MEMBER_DASHBOARD__VIEW,
        rbacPermissions.MOBILE_APP_CMS__MEMBER_DASHBOARD__CREATE,
        rbacPermissions.MOBILE_APP_CMS__MEMBER_DASHBOARD__EDIT,
        rbacPermissions.MOBILE_APP_CMS__MEMBER_DASHBOARD__DELETE,
      ],
    }),
    children: [
      {
        path: path.MEMBER_DASHBOARD_CREATE_NEW,
        requiredPermissions: [
          rbacPermissions.MOBILE_APP_CMS__MEMBER_DASHBOARD__CREATE,
        ],
        element: <MemberDashboardCreateNew />,
      },
      {
        path: path.MEMBER_DASHBOARD_MANAGE_LIVE,
        requiredPermissions: [
          rbacPermissions.MOBILE_APP_CMS__MEMBER_DASHBOARD__VIEW,
        ],
        element: <MemberDashboard />,
      },
    ],
  },
  {
    path: path.MOBILE_APP_CMS,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.MOBILE_APP_CMS__EVENTS__VIEW,
        rbacPermissions.MOBILE_APP_CMS__ARTICLES__VIEW,
      ],
    }),
    children: [
      {
        path: path.ARTICLE,
        requiredPermissions: [rbacPermissions.MOBILE_APP_CMS__ARTICLES__VIEW],
        element: <Article />,
      },
      {
        path: path.EVENTS,
        requiredPermissions: [rbacPermissions.MOBILE_APP_CMS__EVENTS__VIEW],
        element: <Event />,
      },
    ],
  },
  {
    path: `${path.MOBILE_APP_CMS}/${path.MEDIA_LIBRARY}`,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.MOBILE_APP_CMS__MEDIA_LIBRARY__VIEW,
        rbacPermissions.MOBILE_APP_CMS__MEDIA_LIBRARY__CREATE,
      ],
    }),
    children: [
      {
        path: path.UPLOAD_NEW,
        requiredPermissions: [
          rbacPermissions.MOBILE_APP_CMS__MEDIA_LIBRARY__CREATE,
        ],
        element: <UploadNew />,
      },
      {
        path: path.MOBILE_ASSETS,
        requiredPermissions: [
          rbacPermissions.MOBILE_APP_CMS__MEDIA_LIBRARY__VIEW,
        ],
        element: <ManageAssets />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.POINTS_TRANSACTIONS__LISTING__VIEW],
    }),
    children: [
      {
        path: `${path.ALL_TRANSACTION}?membershipId=:id`,
        element: <AllTransaction />,
      },
    ],
  },
  {
    path: path.REFERRAL_PROGRAMME,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.REFERRAL_PROGRAMME__PARTNERSHIPS__VIEW,
        rbacPermissions.REFERRAL_PROGRAMME__ISLANDER_MEMBERS__VIEW,
        rbacPermissions.REFERRAL_PROGRAMME__REFERRAL_TRANSACTIONS__VIEW,
      ],
    }),
    children: [
      {
        path: `${path.PARTNERSHIP}`,
        element: <Partnership />,
        requiredPermissions: [
          rbacPermissions.REFERRAL_PROGRAMME__PARTNERSHIPS__VIEW,
        ],
      },
      {
        path: `${path.ISLANDER_MEMBER}`,
        element: <ReferralIslanderMember />,
        requiredPermissions: [
          rbacPermissions.REFERRAL_PROGRAMME__ISLANDER_MEMBERS__VIEW,
        ],
      },
      {
        path: `${path.REFERRAL_TRANSACTIONS}`,
        element: <ReferralTransactions />,
        requiredPermissions: [
          rbacPermissions.REFERRAL_PROGRAMME__REFERRAL_TRANSACTIONS__VIEW,
        ],
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.VOUCHER_TRANSACTIONS__LISTING__VIEW,
      ],
    }),
    children: [
      {
        path: path.VOUCHER_TRANSACTION_HISTORY,
        element: <VoucherTransactionHistory />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.VOUCHER_TRANSACTIONS__LISTING__VIEW,
      ],
    }),
    children: [
      {
        path: `${path.VOUCHER_TRANSACTION_HISTORY}/:id`,
        element: <VoucherTransactionHistoryDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.VOUCHERS__LISTING__VIEW,
        rbacPermissions.VOUCHERS__FEATURED_VOUCHERS__VIEW,
        rbacPermissions.VOUCHERS__TARGETED_DISTRIBUTION_LOGS__VIEW,
        rbacPermissions.VOUCHERS__VOUCHER_CATEGORIES__VIEW,
        rbacPermissions.VOUCHERS__VOUCHER_CATEGORIES__CREATE,
      ],
    }),
    children: [
      {
        path: path.VOUCHER,
        element: <Voucher />,
        requiredPermissions: [rbacPermissions.VOUCHERS__LISTING__VIEW],
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.VOUCHERS__FEATURED_VOUCHERS__VIEW],
    }),
    children: [
      {
        path: `${path.VOUCHER}/${path.FEATURED_VOUCHER}`,
        element: <FeaturedVoucher />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.VOUCHERS__TARGETED_DISTRIBUTIONS__EXECUTE,
      ],
    }),
    children: [
      {
        path: `${path.VOUCHER}/${path.TARGETED_DISTRIBUTIONS}`,
        element: <TargetedDistributions />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.VOUCHERS__TARGETED_DISTRIBUTION_LOGS__VIEW,
      ],
    }),
    children: [
      {
        path: `${path.VOUCHER}/${path.DISTRIBUTION_LOGS}`,
        element: <DistributionLogs />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.VOUCHERS__FEATURED_VOUCHERS__VIEW],
    }),
    children: [
      {
        path: `${path.VOUCHER}/${path.DISTRIBUTION_LOGS}`,
        element: <FeaturedVoucher />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.VOUCHERS__VOUCHER_CATEGORIES__VIEW],
    }),
    children: [
      {
        path: `${path.VOUCHER}/${path.VOUCHER_CATEGORIES}/${path.VOUCHER_CATEGORIES_MANAGE}`,
        element: <VoucherCategories />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.VOUCHERS__VOUCHER_CATEGORIES__CREATE,
      ],
    }),
    children: [
      {
        path: `${path.VOUCHER}/${path.VOUCHER_CATEGORIES}/${path.VOUCHER_CATEGORIES_ADD_NEW}`,
        element: <AddNew />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.VOUCHERS__FEATURED_VOUCHERS__VIEW],
    }),
    children: [
      {
        path: `${path.VOUCHER}/${path.FEATURED_VOUCHER}/:id`,
        element: <FeaturedVoucher />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.AUDIT__ADMIN_LOGS__VIEW],
    }),
    children: [
      {
        path: path.AUDIT_LOGS,
        element: <AuditLogs />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [rbacPermissions.AUDIT__MEMBER_LOGS__VIEW],
    }),
    children: [
      {
        path: path.AUDIT_LOGS_MEMBERS,
        element: <AuditLogsMembers />,
      },
    ],
  },
  {
    path: path.AUTH,
    element: <AuthLayout />,
    children: [
      {
        path: path.SIGN_IN,
        element: <SignIn />,
      },
      {
        path: path.SIGN_UP,
        element: <SignUp />,
      },
      {
        path: path.RESET_PASSWORD,
        element: <ResetPassword />,
      },
      {
        path: path.CHANGE_PASSWORD,
        element: <ResetPassword isChangePassword />,
      },
      {
        path: path.RESET_PASSWORD_SUCCESS,
        element: <ResetPasswordSuccess />,
      },
      {
        path: path.CHANGE_PASSWORD_SUCCESS,
        element: <ChangePasswordSuccess />,
      },
      {
        path: path.FA_VERITICATION,
        element: <FAVerification />,
      },
      {
        path: path.FORGOT_PASSWORD,
        element: <ForgotPassword />,
      },
      {
        path: path.PASSWORD_RESET_EXPIRED,
        element: <ResetPasswordExpired />,
      },
      {
        path: path[404],
        element: <Page404 />,
      },
      {
        path: path[500],
        element: <Page500 />,
      },
    ],
  },
  // detail
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.ADMINISTRATORS__LISTING__VIEW,
        rbacPermissions.ADMINISTRATORS__LISTING__EDIT_ADMINISTRATORS,
        rbacPermissions.ADMINISTRATORS__LISTING__EDIT_MERCHANTS,
        rbacPermissions.ADMINISTRATORS__LISTING__DELETE_ADMINISTRATORS,
        rbacPermissions.ADMINISTRATORS__LISTING__DELETE_MERCHANTS,
      ],
    }),
    children: [
      {
        path: `${path.PORTAL_USERS}/:id`,
        element: <SDCAdministratorsDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.ISLANDER_MEMBERS__LISTING__VIEW,
        rbacPermissions.ISLANDER_MEMBERS__LISTING__EDIT,
        rbacPermissions.ISLANDER_MEMBERS__LISTING__DELETE,
      ],
    }),
    children: [
      {
        path: `${path.ISLAND_MEMBERS}/:id`,
        element: <IslanderMembersDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.ISLAND_PARTNERS__LISTING__VIEW,
        rbacPermissions.ISLAND_PARTNERS__LISTING__EDIT,
      ],
    }),
    children: [
      {
        path: `${path.ISLAND_PARTNERS}/:id`,
        element: <IslandPartnerDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.POINTS_TRANSACTIONS__LISTING__VIEW,
        rbacPermissions.POINTS_TRANSACTIONS__LISTING__EDIT,
      ],
    }),
    children: [
      {
        path: `${path.ALL_TRANSACTION}/:id`,
        element: <AllTransactionDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.POINTS_TRANSACTIONS__LISTING__VIEW,
        rbacPermissions.POINTS_TRANSACTIONS__LISTING__EDIT,
      ],
    }),
    children: [
      {
        path: `${path.ALL_TRANSACTION}/:id?membershipId=:id`,
        element: <AllTransactionDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.POINTS_TRANSACTIONS__LISTING__VIEW,
        rbacPermissions.POINTS_TRANSACTIONS__LISTING__EDIT,
      ],
    }),
    children: [
      {
        path: `${path.ALL_TRANSACTION}/:id?merchantId=:id`,
        element: <AllTransactionDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.REFERRAL_PROGRAMME__PARTNERSHIPS__VIEW,
        rbacPermissions.REFERRAL_PROGRAMME__PARTNERSHIPS__EDIT,
        rbacPermissions.REFERRAL_PROGRAMME__PARTNERSHIPS__DELETE,
      ],
    }),
    children: [
      {
        path: `${path.REFERRAL_PROGRAMME}/${path.PARTNERSHIP}/:id`,
        element: <PartnershipDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.REFERRAL_PROGRAMME__REFERRAL_TRANSACTIONS__VIEW,
      ],
    }),
    children: [
      {
        path: `${path.REFERRAL_PROGRAMME}/${path.REFERRAL_TRANSACTIONS}/:id`,
        element: <ReferralTransactionDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.VOUCHERS__LISTING__VIEW,
        rbacPermissions.VOUCHERS__LISTING__EDIT,
        rbacPermissions.VOUCHERS__LISTING__DELETE,
      ],
    }),
    children: [
      {
        path: `${path.VOUCHER}/:id`,
        element: <VoucherDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: withAuth({
      node: <DashboardLayout />,
      requiredPermissions: [
        rbacPermissions.VOUCHERS__LISTING__CREATE,
        rbacPermissions.VOUCHERS__VOUCHER_CATEGORIES__CREATE,
      ],
    }),
    children: [
      {
        path: `${path.ADD_NEW_VOUCHER}`,
        element: <VoucherDetail />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: <DashboardLayout />,
    children: [
      {
        path: `${path.NO_ACCESS}`,
        element: <NoAccess />,
      },
    ],
  },

  {
    path: '*',
    element: <AuthLayout />,
    children: [
      {
        path: '*',
        element: <Page404 />,
      },
    ],
  },
  {
    path: path.DEFAULT,
    element: <DashboardLayout />,
    children: [
      {
        path: `${path.CHANGE_LOG}`,
        element: <ChangeLog />,
      },
    ],
  },
];

export default routes;
